import { getLocale } from '@cvent/attendee-hub-locales';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import type { GetServerSidePropsContext } from 'next';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { setLocaleCookieUsingContext } from '@server/setLocaleCookieUsingContext';
import { GET_EVENT_LOCALES_INFO } from '@graphql/operations/event/getEventLocalesInfo';
import { checkAndReturnEventIdLocale, checkIfLocaleExists } from '../../locales';

const LOG = LoggerFactory.create('resolveLocale');

type EventLocales = Parameters<typeof getLocale>[0]; // TODO expose EventLocale type from attendee-hub-locales
type EventLocaleInfoResponse = {
  supportedEventLocales?: EventLocales;
  browserLocaleDetection?: boolean;
};

export interface LocaleCookie {
  [eventId: string]: string;
}

async function getLocaleDataFromLocaleInfoQuery(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  eventId: string,
  environment: string
): Promise<EventLocaleInfoResponse> {
  try {
    const response = await apolloClient.query({
      query: GET_EVENT_LOCALES_INFO,
      variables: {
        eventId,
        environment
      }
    });
    return response?.data?.eventLocaleInfo || {};
  } catch (error) {
    LOG.error('Error fetching locale data', error);
  }
  return {};
}

export const detectBrowserLocale = async (
  ctx: GetServerSidePropsContext,
  apolloClient: ApolloClient<NormalizedCacheObject>,
  eventId: string,
  environment: string
): Promise<{ browserLocale: string; eventLocales: EventLocales }> => {
  let detectLocale;
  let eventLocales: EventLocales;
  try {
    const data = await getLocaleDataFromLocaleInfoQuery(apolloClient, eventId, environment);
    const { supportedEventLocales = [], browserLocaleDetection } = data;
    eventLocales = supportedEventLocales;
    const isEventMultiLingual = supportedEventLocales?.length > 1;
    if (isEventMultiLingual && browserLocaleDetection) {
      // event is multilingual && browser detection is on
      const acceptedLanguages = ctx.req.headers['accept-language'];
      detectLocale = getLocale(supportedEventLocales, acceptedLanguages);
    }
  } catch (error) {
    LOG.error('Error occurred while detecting browser locale', error);
  }
  return { browserLocale: detectLocale?.cultureCode, eventLocales };
};

export default async function resolveLocale(
  context: GetServerSidePropsContext,
  apolloClient: ApolloClient<NormalizedCacheObject>,
  eventLocale: string,
  eventId: string,
  environment: string
): Promise<string> {
  let resolvedLocale = eventLocale;
  // Use cookie locale when available
  const attendeeLocale = checkAndReturnEventIdLocale(context.req.cookies, eventId);
  if (
    attendeeLocale &&
    // Fix accidentally setting cookie to 'undefined'
    attendeeLocale !== 'undefined'
  ) {
    if (checkIfLocaleExists(attendeeLocale)) {
      return attendeeLocale;
    }
    // locale cookie is present but corrupted.. use the event's base locale and update the cookie to correct value
    // could happen in case attendee intentionally tries to manipulate locale cookie
    setLocaleCookieUsingContext(context, eventId, eventLocale);
    return eventLocale;
  }

  const { browserLocale } = await detectBrowserLocale(context, apolloClient, eventId, environment);
  resolvedLocale = browserLocale || eventLocale;
  setLocaleCookieUsingContext(context, eventId, resolvedLocale);
  return resolvedLocale;
}
